import { useRef, useCallback, useMemo, useState } from "react";
import MapGL, { Source, Layer, Popup, MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import type { LayerProps } from "react-map-gl/maplibre";
import type { MapLayerMouseEvent } from "maplibre-gl";
import type { FeatureCollection } from "geojson";
import { Box, IconButton, Typography } from "@mui/material";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import bbox from "@turf/bbox";
import { DEFAULT_TILE_SOURCE } from "../JobMap/tileSources";
import { Robot } from "../../../models";

interface RobotMapProps {
	robots: Robot[];
	height?: number | string;
}

interface RobotPopup {
	lng: number;
	lat: number;
	robot: Robot;
}

const pointCircleLayer: LayerProps = {
	id: "robot-points",
	type: "circle",
	paint: {
		"circle-radius": 8,
		"circle-color": "#4caf50",
		"circle-stroke-width": 2,
		"circle-stroke-color": "#fff",
	},
};

const rawTrailLayer: LayerProps = {
	id: "robot-raw-trail",
	type: "line",
	paint: {
		"line-color": "#1976d2",
		"line-width": 2,
		"line-opacity": 0.7,
	},
};

const pointLabelLayer: LayerProps = {
	id: "robot-labels",
	type: "symbol",
	layout: {
		"text-field": ["get", "name"],
		"text-size": 12,
		"text-offset": [0, -1.5],
		"text-anchor": "bottom",
		"text-allow-overlap": false,
	},
	paint: {
		"text-color": "#333",
		"text-halo-color": "#fff",
		"text-halo-width": 1.5,
	},
};

export function RobotMap({ robots, height = 500 }: RobotMapProps) {
	const mapRef = useRef<MapRef>(null);
	const [popup, setPopup] = useState<RobotPopup | null>(null);
	const [fullscreen, setFullscreen] = useState(false);

	const pointFC: FeatureCollection = useMemo(
		() => ({
			type: "FeatureCollection",
			features: robots
				.filter((r) => r.latitude && r.longitude)
				.map((r) => ({
					type: "Feature" as const,
					geometry: {
						type: "Point" as const,
						coordinates: [r.longitude, r.latitude],
					},
					properties: {
						id: r.id,
						name: r.name,
						latitude: r.latitude,
						longitude: r.longitude,
					},
				})),
		}),
		[robots],
	);

	const rawFC: FeatureCollection = useMemo(
		() => ({
			type: "FeatureCollection",
			features: robots
				.filter((r) => r.raw && r.raw.length > 1)
				.map((r) => ({
					type: "Feature" as const,
					geometry: {
						type: "LineString" as const,
						coordinates: r
							.raw!.slice()
							.sort((a, b) => a.timestamp - b.timestamp)
							.map((p) => [parseFloat(p.longitude), parseFloat(p.latitude)]),
					},
					properties: { id: r.id, name: r.name },
				})),
		}),
		[robots],
	);

	const allFC: FeatureCollection = useMemo(
		() => ({
			type: "FeatureCollection",
			features: [...pointFC.features, ...rawFC.features],
		}),
		[pointFC, rawFC],
	);

	const fitBounds = useCallback(() => {
		const map = mapRef.current;
		if (!map || allFC.features.length === 0) return;
		const [minLng, minLat, maxLng, maxLat] = bbox(allFC);
		map.fitBounds([minLng, minLat, maxLng, maxLat], { padding: 60, maxZoom: 14 });
	}, [allFC]);

	const handleClick = useCallback(
		(e: MapLayerMouseEvent) => {
			const feature = e.features?.[0];
			if (!feature) {
				setPopup(null);
				return;
			}

			const robotId = feature.properties?.id as string;
			const robot = robots.find((r) => r.id === robotId);
			if (robot) {
				setPopup({ lng: e.lngLat.lng, lat: e.lngLat.lat, robot });
			}
		},
		[robots],
	);

	const handleMouseEnter = useCallback(() => {
		const map = mapRef.current;
		if (map) map.getCanvas().style.cursor = "pointer";
	}, []);

	const handleMouseLeave = useCallback(() => {
		const map = mapRef.current;
		if (map) map.getCanvas().style.cursor = "";
	}, []);

	if (robots.length === 0) {
		return (
			<Box
				sx={{
					height,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					No robots found
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height: fullscreen ? "100vh" : height,
				borderRadius: fullscreen ? 0 : 1,
				overflow: "hidden",
				position: fullscreen ? "fixed" : "relative",
				...(fullscreen && { top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300 }),
			}}
		>
			<IconButton
				size="small"
				onClick={() => {
					setFullscreen((prev) => !prev);
					setTimeout(() => {
						mapRef.current?.resize();
						fitBounds();
					}, 50);
				}}
				sx={{
					position: "absolute",
					top: 8,
					right: 8,
					zIndex: 1,
					bgcolor: "background.paper",
					boxShadow: 1,
					"&:hover": { bgcolor: "action.hover" },
				}}
			>
				{fullscreen ? <FullscreenExit /> : <Fullscreen />}
			</IconButton>
			<MapGL
				ref={mapRef}
				initialViewState={{ latitude: 40, longitude: -95, zoom: 4 }}
				style={{ width: "100%", height: "100%" }}
				mapStyle={DEFAULT_TILE_SOURCE.style}
				onLoad={fitBounds}
				onClick={handleClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				interactiveLayerIds={["robot-points"]}
			>
				{rawFC.features.length > 0 && (
					<Source id="robot-raw-trail" type="geojson" data={rawFC}>
						<Layer {...rawTrailLayer} />
					</Source>
				)}
				{pointFC.features.length > 0 && (
					<Source id="robot-points" type="geojson" data={pointFC}>
						<Layer {...pointCircleLayer} />
						<Layer {...pointLabelLayer} />
					</Source>
				)}
				{popup && (
					<Popup
						longitude={popup.lng}
						latitude={popup.lat}
						onClose={() => setPopup(null)}
						closeOnClick={false}
						anchor="bottom"
						maxWidth="250px"
					>
						<Box sx={{ minWidth: 140 }}>
							<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
								{popup.robot.name}
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
								<strong>Lat:</strong> {popup.robot.latitude.toFixed(6)}
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
								<strong>Lng:</strong> {popup.robot.longitude.toFixed(6)}
							</Typography>
						</Box>
					</Popup>
				)}
			</MapGL>
		</Box>
	);
}
